import { IForm } from 'core-components/classes/models/IForm'
import { HttpHeaders, HttpRequest } from 'core/classes/http'
import { TResponse } from 'core/classes/ws/WebSocketClient'

import { REQUEST_TIMEOUT_MS } from './requestTimeout'

interface ICallJSendArgs {
  method: 'get' | 'post' | 'put' | 'delete'
  url: string
  payload?: IForm
  linkToken?: string
}

interface ITransportResult {
  status?: number
  cancelled?: boolean
  data?: unknown
  responseBody?: unknown
}

function errorEnvelope<T extends Record<string, any>>(message: string): TResponse<T> {
  return { status: 'error', data: {} as T, message }
}

function isEnvelope<T extends Record<string, any>>(value: unknown): value is TResponse<T> {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as { status?: unknown }).status === 'string'
  )
}

function toEnvelope<T extends Record<string, any>>(result: unknown): TResponse<T> {
  const transportResult = (result ?? {}) as ITransportResult

  if (transportResult.cancelled) {
    return errorEnvelope<T>('The request took too long and was cancelled')
  }

  if (isEnvelope<T>(transportResult.responseBody)) {
    return transportResult.responseBody
  }

  if (isEnvelope<T>(transportResult.data)) {
    return transportResult.data
  }

  const transportErrors = (transportResult.data as { Errors?: string } | undefined)?.Errors

  if (transportErrors) {
    return errorEnvelope<T>(transportErrors)
  }

  return errorEnvelope<T>(
    transportResult.status
      ? `Request failed (HTTP ${transportResult.status})`
      : 'The request did not reach the service'
  )
}

export async function callJSend<T extends Record<string, any>>({
  method,
  url,
  payload,
  linkToken,
}: ICallJSendArgs): Promise<TResponse<T>> {
  const headers = new HttpHeaders()

  if (linkToken) {
    headers.add('Authorization', `Bearer ${linkToken}`)
  }

  const abortController = new AbortController()
  const timeoutId = setTimeout(() => abortController.abort(), REQUEST_TIMEOUT_MS)

  try {
    const result = await new HttpRequest(headers)[method]({
      url,
      payload,
      signal: abortController.signal,
    })

    return toEnvelope<T>(result)
  } catch (e: unknown) {
    return errorEnvelope<T>(e instanceof Error ? e.message : 'Request failed')
  } finally {
    clearTimeout(timeoutId)
  }
}
